/**
 * @function StickyBottomBar
 *
 * Dismissal stores a timestamp. Users can reopen via the restore tab.
 * Optional auto-reappear after a merchant-configured number of days.
 */
if (!customElements.get('sticky-bottom-bar')) {
  class StickyBottomBar extends HTMLElement {
    constructor() {
      super();
      this._storageKey = `sticky-bottom-bar-dismissed-${this.dataset.sectionId}`;
      this._shell = null;
      this._restore = null;
      this._closeButton = null;
    }

    connectedCallback() {
      this._shell = this.querySelector('[data-sticky-bar-shell]');
      this._restore = this.querySelector('[data-sticky-bar-restore]');
      this._closeButton = this.querySelector('[data-sticky-bar-close]');

      if (this._closeButton) {
        this._closeButton.addEventListener('click', this._onClose.bind(this));
      }

      if (this._restore) {
        this._restore.addEventListener('click', this._onRestore.bind(this));
      }

      if (this._shouldShowBar()) {
        this._showBar(false);
        return;
      }

      if (!(window.Shopify && Shopify.designMode)) {
        this._showRestore(false);
      }
    }

    _onClose(event) {
      event.preventDefault();
      event.stopPropagation();
      this._setDismissed();
      this._showRestore(true);
    }

    _onRestore(event) {
      event.preventDefault();
      this._clearDismissed();
      this._showBar(true);
    }

    _shouldShowBar() {
      if (window.Shopify && Shopify.designMode) return true;

      const dismissedAt = this._getDismissedAt();
      if (!dismissedAt) return true;

      const expiryDays = parseInt(this.dataset.dismissDays, 10);
      if (!expiryDays || expiryDays <= 0) return false;

      const msPerDay = 86400000;
      return Date.now() - dismissedAt >= expiryDays * msPerDay;
    }

    _getDismissedAt() {
      try {
        const raw = localStorage.getItem(this._storageKey);
        if (!raw) return null;
        if (raw === '1') return Date.now();
        const parsed = JSON.parse(raw);
        return typeof parsed.at === 'number' ? parsed.at : null;
      } catch (e) {
        return null;
      }
    }

    _setDismissed() {
      try {
        localStorage.setItem(this._storageKey, JSON.stringify({ at: Date.now() }));
      } catch (e) {
        /* localStorage unavailable */
      }
    }

    _clearDismissed() {
      try {
        localStorage.removeItem(this._storageKey);
      } catch (e) {
        /* localStorage unavailable */
      }
    }

    _showBar(animate) {
      this.classList.remove('sticky-bottom-bar--collapsed', 'sticky-bottom-bar--dismissed');
      this.removeAttribute('hidden');
      this.style.display = '';

      if (this._shell) {
        this._shell.hidden = false;
      }

      if (this._restore) {
        this._restore.hidden = true;
      }

      if (!animate) return;

      this.classList.add('sticky-bottom-bar--entering');
      this.addEventListener(
        'transitionend',
        () => this.classList.remove('sticky-bottom-bar--entering'),
        { once: true }
      );
    }

    _showRestore(animate) {
      if (!this._restore || this.dataset.showRestore !== 'true') {
        this._hideCompletely(animate);
        return;
      }

      this.classList.remove('sticky-bottom-bar--dismissed');
      this.classList.add('sticky-bottom-bar--collapsed');
      this.removeAttribute('hidden');
      this.style.display = '';

      if (this._shell) {
        this._shell.hidden = true;
      }

      this._restore.hidden = false;

      if (!animate) return;

      this.classList.add('sticky-bottom-bar--entering');
      this.addEventListener(
        'transitionend',
        () => this.classList.remove('sticky-bottom-bar--entering'),
        { once: true }
      );
    }

    _hideCompletely(animate) {
      this.classList.remove('sticky-bottom-bar--collapsed');
      this.classList.add('sticky-bottom-bar--dismissed');
      this.setAttribute('hidden', '');

      if (this._shell) {
        this._shell.hidden = true;
      }

      if (this._restore) {
        this._restore.hidden = true;
      }

      if (!animate) {
        this.style.display = 'none';
        return;
      }

      this.addEventListener(
        'transitionend',
        () => {
          this.style.display = 'none';
        },
        { once: true }
      );
    }
  }

  customElements.define('sticky-bottom-bar', StickyBottomBar);
}
